import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { EmploymentContractField } from './EmploymentContractService';

type EmploymentContractHtmlFormProps = {
  html: string;
  fields: EmploymentContractField[];
  values: Record<string, any>;
  errors: Record<string, string>;
  focusRequest?: { fieldName: string; requestId: number } | null;
  loading?: boolean;
  readOnly?: boolean;
  onChange: (fieldName: string, value: any) => void;
};

const isDateField = (field?: EmploymentContractField) =>
  field?.fieldTypeEnumId === 'DBFFAT_date-short' || field?.fieldTypeEnumId === 'DBFFAT_date-long';

const toDateDisplayValue = (value: any) => {
  if (!value) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const day = String(value.getDate()).padStart(2, '0');
    const month = String(value.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${value.getFullYear()}`;
  }
  const text = String(value);
  const viDate = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (viDate) {
    return `${viDate[1].padStart(2, '0')}/${viDate[2].padStart(2, '0')}/${viDate[3]}`;
  }
  if (!/^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/.test(text)) return text;
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return text;
  const day = String(parsed.getDate()).padStart(2, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${parsed.getFullYear()}`;
};

const normalizeDateTyping = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const fromDateDisplayValue = (value: string) => {
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return value;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year
    || parsed.getMonth() !== month - 1
    || parsed.getDate() !== day
  ) return value;
  return parsed;
};

export const EmploymentContractHtmlForm: React.FC<EmploymentContractHtmlFormProps> = ({
  html,
  fields,
  values,
  errors,
  focusRequest = null,
  loading = false,
  readOnly = false,
  onChange
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  const cleanupRef = useRef<(() => void) | null>(null);
  const [frameHeight, setFrameHeight] = useState(1200);
  const [activeFieldIndex, setActiveFieldIndex] = useState(-1);
  const [fieldCount, setFieldCount] = useState(0);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const fieldMap = useRef<Map<string, EmploymentContractField>>(new Map());
  useEffect(() => {
    fieldMap.current = new Map(fields.map(field => [field.fieldName, field]));
  }, [fields]);

  const resizeFrame = useCallback(() => {
    const documentRef = iframeRef.current?.contentDocument;
    if (!documentRef) return;
    const height = Math.max(
      1123,
      documentRef.body?.scrollHeight || 0,
      documentRef.documentElement?.scrollHeight || 0
    );
    setFrameHeight(height + 2);
  }, []);

  const getNavigableFields = useCallback(() => {
    const documentRef = iframeRef.current?.contentDocument;
    if (!documentRef) return [];

    const seenFields = new Set<string>();
    return Array.from(
      documentRef.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[data-field]')
    ).filter(element => {
      const fieldName = element.dataset.field;
      if (!fieldName || seenFields.has(fieldName) || element.disabled) return false;
      seenFields.add(fieldName);
      return true;
    });
  }, []);

  const focusField = useCallback((index: number) => {
    const navigableFields = getNavigableFields();
    if (!navigableFields.length) return;

    const nextIndex = Math.max(0, Math.min(index, navigableFields.length - 1));
    const element = navigableFields[nextIndex];
    const iframe = iframeRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (!iframe || !scrollContainer) return;

    const elementTop = iframe.offsetTop
      + element.getBoundingClientRect().top
      + (iframe.contentWindow?.scrollY || 0);
    scrollContainer.scrollTo({
      top: Math.max(0, elementTop - (scrollContainer.clientHeight / 2)),
      behavior: 'smooth'
    });
    setActiveFieldIndex(nextIndex);
    window.setTimeout(() => element.focus({ preventScroll: true }), 250);
  }, [getNavigableFields]);

  const moveField = useCallback((direction: -1 | 1) => {
    const navigableFields = getNavigableFields();
    if (!navigableFields.length) return;

    const documentRef = iframeRef.current?.contentDocument;
    const focusedIndex = navigableFields.findIndex(element => element === documentRef?.activeElement);
    const currentIndex = focusedIndex >= 0 ? focusedIndex : activeFieldIndex;
    const nextIndex = currentIndex < 0
      ? (direction > 0 ? 0 : navigableFields.length - 1)
      : currentIndex + direction;
    focusField(nextIndex);
  }, [activeFieldIndex, focusField, getNavigableFields]);

  const syncFrame = useCallback(() => {
    const documentRef = iframeRef.current?.contentDocument;
    if (!documentRef) return;

    documentRef.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('[data-field]').forEach(element => {
      const fieldName = element.dataset.field || '';
      const field = fieldMap.current.get(fieldName);
      const value = values[fieldName];
      const nextValue = isDateField(field) ? toDateDisplayValue(value) : String(value ?? '');
      if (element.value !== nextValue) element.value = nextValue;
      element.required = field?.isRequired === 'Y';
      element.readOnly = readOnly;
      element.classList.toggle('field-error', !!errors[fieldName]);
      element.title = errors[fieldName] || field?.fieldLabel || fieldName;
      element.setAttribute('aria-invalid', errors[fieldName] ? 'true' : 'false');
    });

    window.setTimeout(resizeFrame, 0);
  }, [errors, readOnly, resizeFrame, values]);

  useEffect(() => {
    syncFrame();
  }, [syncFrame]);

  useEffect(() => {
    const firstError = Object.keys(errors)[0];
    if (!firstError) return;
    const errorIndex = getNavigableFields().findIndex(element => element.dataset.field === firstError);
    if (errorIndex >= 0) focusField(errorIndex);
  }, [errors, focusField, getNavigableFields]);

  useEffect(() => {
    if (!focusRequest?.fieldName) return;
    const fieldIndex = getNavigableFields().findIndex(element => element.dataset.field === focusRequest.fieldName);
    if (fieldIndex >= 0) focusField(fieldIndex);
  }, [focusField, focusRequest, getNavigableFields]);

  useEffect(() => () => cleanupRef.current?.(), []);

  const handleLoad = () => {
    cleanupRef.current?.();
    const documentRef = iframeRef.current?.contentDocument;
    if (!documentRef) return;
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;

    const handleInput = (event: Event) => {
      if (readOnly) return;
      const element = event.target as HTMLInputElement | HTMLTextAreaElement;
      const fieldName = element?.dataset?.field;
      if (!fieldName) return;
      const field = fieldMap.current.get(fieldName);
      let value: any = element.value;
      if (isDateField(field)) {
        const normalizedValue = normalizeDateTyping(element.value);
        if (element.value !== normalizedValue) element.value = normalizedValue;
        value = fromDateDisplayValue(normalizedValue);
      }
      onChangeRef.current(fieldName, value);
      window.setTimeout(resizeFrame, 0);
    };

    const handleFocus = (event: Event) => {
      const element = event.target as HTMLInputElement | HTMLTextAreaElement;
      const fieldName = element?.dataset?.field;
      if (!fieldName) return;
      const nextIndex = getNavigableFields().findIndex(item => item.dataset.field === fieldName);
      if (nextIndex >= 0) setActiveFieldIndex(nextIndex);
    };

    documentRef.addEventListener('input', handleInput);
    documentRef.addEventListener('change', handleInput);
    documentRef.addEventListener('focusin', handleFocus);
    cleanupRef.current = () => {
      documentRef.removeEventListener('input', handleInput);
      documentRef.removeEventListener('change', handleInput);
      documentRef.removeEventListener('focusin', handleFocus);
    };
    syncFrame();
    setFieldCount(readOnly ? 0 : getNavigableFields().length);
    setActiveFieldIndex(-1);
  };

  if (loading) {
    return (
      <div className="flex h-full align-items-center justify-content-center" style={{ minHeight: 0 }}>
        <ProgressSpinner style={{ width: 46, height: 46 }} />
      </div>
    );
  }

  return (
    <div
      className="relative h-full border-1 surface-border border-round overflow-hidden"
      style={{ minHeight: 0, background: '#eef1f5' }}
    >
      <div ref={scrollContainerRef} className="h-full overflow-auto">
        <iframe
          ref={iframeRef}
          title="Biểu mẫu hợp đồng lao động"
          srcDoc={html}
          sandbox="allow-same-origin"
          onLoad={handleLoad}
          className="block w-full border-none"
          style={{ height: frameHeight, minWidth: 0, background: '#eef1f5' }}
        />
      </div>
      {!readOnly && <div
        className="absolute flex flex-column align-items-center gap-2 p-2"
        style={{ right: 14, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}
      >
        <Button
          type="button"
          icon="pi pi-chevron-up"
          rounded text
          aria-label="Đi tới trường trước"
          tooltip="Trường trước"
          tooltipOptions={{ position: 'left' }}
          disabled={!fieldCount || activeFieldIndex === 0}
          onClick={() => moveField(-1)}
        />
        <span className="text-sm font-medium white-space-nowrap" aria-live="polite">
          {activeFieldIndex >= 0 ? activeFieldIndex + 1 : '-'} / {fieldCount}
        </span>
        <Button
          type="button"
          icon="pi pi-chevron-down"
          rounded text
          aria-label="Đi tới trường tiếp theo"
          tooltip="Trường tiếp theo"
          tooltipOptions={{ position: 'left' }}
          disabled={!fieldCount || activeFieldIndex === fieldCount - 1}
          onClick={() => moveField(1)}
        />
      </div>}
    </div>
  );
};
